import React from 'react';
import { CircularProgress } from '@heroui/react';

import { store, useAppSelector } from '@/app/store';
import { TotpDeviceInfo } from '@/features/logins/loginsSlice';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';
import Input from '@/components/Common/Input';
import Modal from '@/components/Common/Modals';

import EnrollTwoFA from '@/pages/Login/EnrollTwoFA';

const formatTimestamp = (unix: number | undefined): string => {
  if (!unix || unix <= 0) return '—';
  return new Date(unix * 1000).toLocaleString();
};

const TwoFactor: React.FC = () => {
  const [devices, setDevices] = React.useState<TotpDeviceInfo[] | null>(null);
  const [refreshing, setRefreshing] = React.useState<boolean>(false);
  const [showAdd, setShowAdd] = React.useState<boolean>(false);

  // Remove-device flow state. Keeping it adjacent to the device list keeps
  // this component file self-contained; if it grows further we'd split out
  // a <RemoveDeviceModal /> child.
  const [removeTarget, setRemoveTarget] = React.useState<TotpDeviceInfo | null>(null);
  const [removeCode, setRemoveCode] = React.useState<string>('');
  const [removeError, setRemoveError] = React.useState<string>('');
  const [removing, setRemoving] = React.useState<boolean>(false);

  // Regenerate-recovery-codes flow state. Two phases:
  //   prompt  — modal with TOTP code field
  //   reveal  — show the 10 fresh codes once, require "I saved them" ack
  const [regenPhase, setRegenPhase] = React.useState<'idle' | 'prompt' | 'reveal'>('idle');
  const [regenCode, setRegenCode] = React.useState<string>('');
  const [regenError, setRegenError] = React.useState<string>('');
  const [regenerating, setRegenerating] = React.useState<boolean>(false);
  const [newRecoveryCodes, setNewRecoveryCodes] = React.useState<string[]>([]);
  const [regenCopied, setRegenCopied] = React.useState<boolean>(false);

  // Server tells us how many codes are left after a recovery-code login.
  // If low, show an inline warning prompting regeneration. Only meaningful
  // until the user regenerates or logs out.
  const codesRemaining = useAppSelector((state) => state.logins.recoveryCodesRemaining);

  const refresh = React.useCallback(async () => {
    setRefreshing(true);
    try {
      const list = await store.listDevices();
      setDevices(list);
    } finally {
      setRefreshing(false);
    }
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAddComplete = React.useCallback(() => {
    setShowAdd(false);
    refresh();
  }, [refresh]);

  const handleRegenSubmit = React.useCallback(async () => {
    const trimmed = regenCode.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setRegenError('Enter the current 6-digit code from your authenticator.');
      return;
    }
    setRegenError('');
    setRegenerating(true);
    try {
      const codes = await store.regenerateRecoveryCodes(trimmed);
      if (!codes) {
        setRegenError('Code rejected. Recovery codes can only be regenerated with a current authenticator code.');
        return;
      }
      setNewRecoveryCodes(codes);
      setRegenPhase('reveal');
      setRegenCode('');
    } finally {
      setRegenerating(false);
    }
  }, [regenCode]);

  const handleRegenCopy = React.useCallback(async () => {
    await navigator.clipboard.writeText(newRecoveryCodes.join('\n'));
    setRegenCopied(true);
    setTimeout(() => setRegenCopied(false), 2500);
  }, [newRecoveryCodes]);

  const handleRegenDone = React.useCallback(() => {
    // Clear the codes from memory the moment the user acknowledges them —
    // they're single-use credentials and there is no second-chance view.
    setNewRecoveryCodes([]);
    setRegenPhase('idle');
    setRegenCopied(false);
  }, []);

  const handleRemoveSubmit = React.useCallback(async () => {
    if (!removeTarget) return;
    const trimmed = removeCode.trim();
    if (!/^\d{6}$/.test(trimmed) && !/^[a-z0-9]{3,}-[a-z0-9]{3,}$/i.test(trimmed)) {
      setRemoveError('Enter a 6-digit code from a DIFFERENT enrolled device.');
      return;
    }
    setRemoveError('');
    setRemoving(true);
    try {
      const ok = await store.removeDevice(removeTarget.id, trimmed);
      if (!ok) {
        setRemoveError('Code rejected — must be from a different enrolled device.');
        return;
      }
      setRemoveTarget(null);
      setRemoveCode('');
      refresh();
    } finally {
      setRemoving(false);
    }
  }, [removeTarget, removeCode, refresh]);

  if (showAdd) {
    return (
      <div className="bg-[--Light-Grey] min-h-screen w-full">
        <EnrollTwoFA
          onComplete={handleAddComplete}
          completeLabel="Done"
          onCancel={() => setShowAdd(false)}
        />
      </div>
    );
  }

  return (
    <FlexRow direction="column" className="p-[24px] gap-[16px] max-w-[720px]">
      <h1 className="text-[20px] font-bold">Two-factor authentication</h1>
      <p className="text-[--Dark-Grey] text-[14px]">
        Manage the authenticator apps that can sign in to your account. Each device
        gets its own code; any one of them works on login. You can have up to 16.
      </p>

      <FlexRow align="center" flex={0} className="justify-between">
        <h2 className="text-[16px] font-bold m-0">Enrolled devices</h2>
        <Button caption="Add new device" onClick={() => setShowAdd(true)} />
      </FlexRow>

      { devices === null && refreshing &&
        <FlexRow justify="center" className="p-[24px]">
          <CircularProgress aria-label="loading devices" />
        </FlexRow>
      }

      { devices !== null && devices.length === 0 &&
        <p className="text-[--Dark-Grey] text-[14px]">
          No authenticator devices enrolled. Click "Add new device" to set one up.
        </p>
      }

      { devices !== null && devices.length > 0 &&
        <table className="w-full text-[14px] border-collapse">
          <thead>
            <tr className="text-left border-b border-[--Mid-Grey]">
              <th className="py-[8px] pr-[12px] font-bold">Name</th>
              <th className="py-[8px] pr-[12px] font-bold">Added</th>
              <th className="py-[8px] pr-[12px] font-bold">Last used</th>
              <th className="py-[8px] text-right font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            { devices.map((d) => (
              <tr key={d.id} className="border-b border-[--Light-Grey]">
                <td className="py-[10px] pr-[12px]">{ d.name }</td>
                <td className="py-[10px] pr-[12px] text-[--Dark-Grey]">{ formatTimestamp(d.added_at) }</td>
                <td className="py-[10px] pr-[12px] text-[--Dark-Grey]">{ formatTimestamp(d.last_used_at) }</td>
                <td className="py-[10px] text-right">
                  <button
                    className="text-[--Error] hover:underline text-[13px]"
                    onClick={() => { setRemoveTarget(d); setRemoveCode(''); setRemoveError(''); }}>
                    Remove
                  </button>
                </td>
              </tr>
            )) }
          </tbody>
        </table>
      }

      { devices !== null && devices.length === 1 &&
        <p className="text-[12px] text-[--Dark-Grey]">
          You have only one device enrolled. Removing it isn't allowed from here — an admin
          must disable 2FA for your account if you've lost the device.
        </p>
      }

      { devices !== null && devices.length > 0 &&
        <FlexRow direction="column" flex={0} className="gap-[8px] pt-[16px] border-t border-[--Mid-Grey]">
          <h2 className="text-[16px] font-bold m-0">Recovery codes</h2>
          <p className="text-[--Dark-Grey] text-[13px] m-0">
            Recovery codes are single-use backups for when you lose access to every enrolled
            device. Regenerating issues 10 fresh codes and immediately invalidates any
            previously issued ones. You'll need to enter a current authenticator code to confirm.
          </p>
          { typeof codesRemaining === "number" && codesRemaining <= 3 &&
            <p className="text-[13px] text-[--Error] m-0">
              Only { codesRemaining } recovery code{ codesRemaining === 1 ? '' : 's' } left — regenerate now.
            </p>
          }
          <FlexRow flex={0}>
            <Button caption="Regenerate recovery codes"
                    onClick={() => { setRegenPhase('prompt'); setRegenCode(''); setRegenError(''); }} />
          </FlexRow>
        </FlexRow>
      }

      { regenPhase === 'prompt' &&
        <Modal title="Regenerate recovery codes"
               message="Your existing recovery codes will stop working the moment this succeeds. Enter the current 6-digit code from any enrolled authenticator to confirm."
               onResult={(confirmed) => {
                 if (!confirmed) {
                   setRegenPhase('idle');
                   setRegenCode('');
                   setRegenError('');
                 }
               }}>
          <FlexRow direction="column" className="gap-[12px]">
            <Input value={regenCode}
                   label="6-digit code from authenticator"
                   onValueChange={setRegenCode}
                   type="text"
                   isInvalid={!!regenError}
                   errorMessage={regenError}
                   classNames={{
                     inputWrapper: "px-[16px] py-[8px] rounded-[4px] border-solid border-1 border-[--Mid-Grey]",
                     input: "tracking-[0.15em] font-mono",
                   }} />
            <Button caption={regenerating ? "Regenerating…" : "Generate new codes"}
                    layout={regenerating ? 'warning_bg' : 'default'}
                    isDisabled={regenerating}
                    onClick={handleRegenSubmit} />
          </FlexRow>
        </Modal>
      }

      { regenPhase === 'reveal' &&
        <Modal title="Save your new recovery codes"
               message="These codes replace any previous set. Each one works exactly once. Store them somewhere safe — they will NOT be shown again."
               onResult={() => { /* dismiss is handled by the explicit "I've saved them" button below */ }}>
          <FlexRow direction="column" className="gap-[12px]">
            <div className="grid grid-cols-2 gap-[6px] font-mono text-[13px] bg-[--Light-Grey] p-[12px] rounded-[4px]">
              { newRecoveryCodes.map((c) => (
                <div key={c} className="select-all">{ c }</div>
              )) }
            </div>
            <Button caption={regenCopied ? "Copied!" : "Copy all 10 codes"}
                    onClick={handleRegenCopy} />
            <Button caption="I've saved my recovery codes"
                    onClick={handleRegenDone} />
          </FlexRow>
        </Modal>
      }

      { removeTarget &&
        <Modal title={`Remove "${removeTarget.name}"`}
               message="Enter a code from a DIFFERENT enrolled device to confirm. (We don't accept the code from the device being removed — that's the whole point of multi-device 2FA.)"
               onResult={(confirmed) => {
                 if (!confirmed) {
                   setRemoveTarget(null);
                   setRemoveCode('');
                   setRemoveError('');
                 }
               }}>
          <FlexRow direction="column" className="gap-[12px]">
            <Input value={removeCode}
                   label="Code from a different device"
                   onValueChange={setRemoveCode}
                   type="text"
                   isInvalid={!!removeError}
                   errorMessage={removeError}
                   classNames={{
                     inputWrapper: "px-[16px] py-[8px] rounded-[4px] border-solid border-1 border-[--Mid-Grey]",
                     input: "tracking-[0.15em] font-mono",
                   }} />
            <Button caption={removing ? "Removing…" : "Remove device"}
                    layout={removing ? 'warning_bg' : 'default'}
                    isDisabled={removing}
                    onClick={handleRemoveSubmit} />
          </FlexRow>
        </Modal>
      }
    </FlexRow>
  );
};

export default TwoFactor;
